import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { BLENDE_REDUZIERT_W82, DAUER, bewegungReduziert, kurve, still } from "../welt/bewegung";
import { useWelt } from "../welt/einstellungen";
import { useGeist } from "../welt/geistKontext";

/** `erfolg`: Name aus „Abgelegt in …“, `kopf`: der Kopf der Karte als Vorlage für das Abbild im Platz (W82 Web-5h-N1, Frage 66 = B). */
export type Halt = { id: string; hoehe: number; erfolg?: string; kopf?: HTMLElement | null };

/** W82 Web-5h3 (HW9): nach „Passt“ hält die Startseite die Fläche der Prüfkarte, bis der Gaist geantwortet hat („Danke“,
 *  Escape, Rollen; ohne Antwort sofort frei). Die Karte geht nach dem Nachladen wie bisher (Fokus, nächste Karte), an ihrer
 *  Stelle steht so lange ein leerer Platz gleicher Höhe: nichts rückt unter der Figur und der Blase nach. `halten` erst nach
 *  dem Anstoß der Antwort rufen, sonst ist sie noch nicht offen. R1: solange der Platz hält, rollt der Fokus der gegangenen
 *  Karte die Seite nicht (`data-karte-haelt`, ablegen.ts); geht er, rückt der Inhalt danach über die Lage nach (der „ruhige
 *  Übergang“, Lage-und-Deckkraft-Regel, `wechsel.sortieren` mit `mit`). W82 Web-5h-N1 (Frage 66 = B, D-677): der Platz zeigt den
 *  Erfolgszustand der Karte (`KartenPlatz`); reduziert springt die Lage, der Inhalt danach blendet ein (Blende statt Sprung,
 *  DESIGN-W82 „Reduzierte Bewegung“, 150 ms), `?still` sofort. */
export function useKarteHalten() {
  const geist = useGeist();
  const [welt] = useWelt();
  const [halt, setHalt] = useState<Halt | null>(null);
  const aktuell = useRef<Halt | null>(null);
  const nachruecken = useRef<{ el: HTMLElement; top: number }[] | null>(null);
  useEffect(() => {
    aktuell.current = halt;
    if (!halt) return;
    const wurzel = document.documentElement;
    wurzel.setAttribute("data-karte-haelt", "");
    // R1 (HW9): der Platz bleibt im Fenster stehen, auch ohne Figur an ihm (Randgeist, Satz am Platz): ändert sich darüber etwas
    // (neue Frist im Modul), gleicht der Bildlauf aus; gemessen wird die Lage im Dokument, eigenes Rollen zählt nicht. Steht der
    // Gaist an ihm (`data-geist-haelt`), gleicht seine Ebene aus (Mitgleiten), hier nichts doppelt
    const lage = () => {
      const el = document.querySelector(`[data-geist-bezug="${CSS.escape(halt.id)}"]`);
      return el ? el.getBoundingClientRect().top + scrollY : null;
    };
    let vorher = lage();
    const pruefen = () => {
      const jetzt = lage();
      if (jetzt === null) return;
      if (vorher !== null && Math.abs(jetzt - vorher) > 0.5 && !wurzel.hasAttribute("data-geist-haelt")) scrollBy({ top: jetzt - vorher, behavior: "instant" });
      vorher = jetzt;
    };
    let takt = 0;
    const schritt = () => {
      pruefen();
      takt = requestAnimationFrame(schritt);
    };
    takt = requestAnimationFrame(schritt);
    // gleich nach dem Einhängen von React, bevor der Browser zeichnet
    const aenderung = typeof MutationObserver === "undefined" ? null : new MutationObserver(pruefen);
    aenderung?.observe(document.body, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(takt);
      aenderung?.disconnect();
      wurzel.removeAttribute("data-karte-haelt");
    };
  }, [halt]);
  useLayoutEffect(() => {
    const liste = nachruecken.current;
    if (halt || !liste) return;
    nachruecken.current = null;
    if (still()) return;
    if (bewegungReduziert()) {
      // Blende statt Sprung: was nachrückt, steht sofort an seiner Lage und blendet ein
      for (const { el, top } of liste) {
        if (el.isConnected && Math.abs(top - el.getBoundingClientRect().top) > 0.5 && typeof el.animate === "function") {
          el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: BLENDE_REDUZIERT_W82, easing: "linear" });
        }
      }
      return;
    }
    for (const { el, top } of liste) {
      const dy = top - el.getBoundingClientRect().top;
      // zur eigenen Lage addiert (composite add): ein Element mit eigener Verschiebung behält sie
      if (el.isConnected && Math.abs(dy) > 0.5 && typeof el.animate === "function") {
        el.animate([{ transform: `translateY(${dy}px)` }, { transform: "translateY(0)" }], { duration: DAUER.wechsel.sortieren[welt.fassung], easing: kurve("mit"), composite: "add" });
      }
    }
  }, [halt, welt.fassung]);
  function halten(id: string, hoehe: number, erfolg?: { name: string; kopf: HTMLElement | null }) {
    setHalt({ id, hoehe, erfolg: erfolg?.name, kopf: erfolg?.kopf });
    geist.nachAntwort(() => {
      const platz = aktuell.current?.id === id ? document.querySelector(`[data-testid="karte-platz"][data-geist-bezug="${CSS.escape(id)}"]`) : null;
      nachruecken.current = platz ? folgende(platz).map((el) => ({ el, top: el.getBoundingClientRect().top })) : null;
      setHalt((alt) => (alt?.id === id ? null : alt));
    });
  }
  return { halt, halten };
}

/** Was nach dem Platz im Fluss steht: seine späteren Geschwister und die späteren Geschwister seiner Vorfahren bis `main`. */
function folgende(platz: Element): HTMLElement[] {
  const aus: HTMLElement[] = [];
  for (let e: Element | null = platz; e && e.tagName !== "MAIN" && e !== document.body; e = e.parentElement) {
    for (let s = e.nextElementSibling; s; s = s.nextElementSibling) if (s instanceof HTMLElement) aus.push(s);
  }
  return aus;
}
